import React, { lazy } from "react";
import { useRoutes } from "react-router-dom";
import { useSelector } from "react-redux";

//Project imports
import config from "../config";
import Loadable from "../components/Loader/Loadable";
import AuthGuard from "../guards/AuthGuard";
import { useUserData } from "../hooks/useUserData";
import { CircularProgress, Typography, Box } from "@mui/material";
import { styled } from "@mui/material/styles";

const LoadingContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  minHeight: "200px",
  flexDirection: "column",
  gap: theme.spacing(2),
}));
// AUTH PAGES
const Conta = Loadable(lazy(() => import("../pages/Conta/index.js")));
const Fornecimento = Loadable(
  lazy(() => import("../pages/Fornecimento/index.js"))
);
const Faq = Loadable(lazy(() => import("../pages/Faq/index.js")));
const NotFound = Loadable(lazy(() => import("../pages/NotFound/index.js")));
const FornecimentoMainLayout = Loadable(
  lazy(() => import("../layout/fornecimento.js"))
);
const ContaMainLayout = Loadable(lazy(() => import("../layout/conta.js")));

export default function Routes() {
  const { user, isLoading } = useUserData();
  const dataRef = useSelector((state) => state.Filter.selectedDataRef);

  // Define loading route
  const LoadingRoute = {
    path: "*",
    element: (
      <>
        <LoadingContainer>
          <CircularProgress size={32} />
          <Typography variant="h4" color="text.secondary">
            Carregando detalhes da fatura...
          </Typography>
        </LoadingContainer>
      </>
    ), // ou seu componente de loading
  };

  // Define all routes conditionally based on user data availability
  const getRoutes = () => {
    if (isLoading || !user) {
      return [LoadingRoute];
    }

    const AuthRoutes = {
      path: "/",
      element: (
        <AuthGuard>
          <ContaMainLayout user={user} dataRef={dataRef} />
        </AuthGuard>
      ),
      children: [
        {
          path: "/home",
          element: <Conta user={user} dataRef={dataRef} />,
        },
      ],
    };

    const MainRoutes = {
      path: "/faturas",
      element: (
        <AuthGuard>
          <FornecimentoMainLayout user={user} dataRef={dataRef} />
        </AuthGuard>
      ),
      children: [
        {
          path: "/faturas/fornecimento",
          element: <Fornecimento user={user} dataRef={dataRef} />,
        },
        {
          path: "/faturas/contas",
          element: <Conta user={user} dataRef={dataRef} />,
        },
        {
          path: "/faturas/faq",
          element: <Faq />,
        },
      ],
    };

    // Direct fornecimento route for navigation from DataGrid
    const FornecimentoDirectRoute = {
      path: "/fornecimento",
      element: (
        <AuthGuard>
          <FornecimentoMainLayout user={user} dataRef={dataRef} />
        </AuthGuard>
      ),
      children: [
        {
          index: true,
          element: <Fornecimento user={user} dataRef={dataRef} />,
        },
      ],
    };

    // Catch-all route for 404 pages
    const NotFoundRoute = {
      path: "*",
      element: <NotFound />,
    };

    // Root redirect route
    const RootRedirectRoute = {
      path: "/",
      element: (
        <AuthGuard>
          <ContaMainLayout user={user} dataRef={dataRef} />
        </AuthGuard>
      ),
      children: [
        {
          index: true,
          element: <Conta user={user} dataRef={dataRef} />,
        },
      ],
    };

    return [
      AuthRoutes,
      MainRoutes,
      FornecimentoDirectRoute,
      RootRedirectRoute,
      NotFoundRoute,
    ];
  };

  return useRoutes(getRoutes(), config.basename);
}
